import {FC, useEffect, useState} from 'react'
import {api, ApiError, GalleryItem, listingImageSrc} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {PageHead} from '../../components/PageHead'
import {useToast} from '../../components/Toast'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 9,
  fontSize: 14, background: '#fff', boxSizing: 'border-box', outline: 'none',
}
const iconBtn: React.CSSProperties = {
  width: 34, height: 34, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff',
  color: INK, cursor: 'pointer', fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
}

const SiteGalleryPage: FC = () => {
  const toast = useToast()
  const [items, setItems] = useState<GalleryItem[] | null>(null)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    api.getSiteGallery().then((r) => setItems(r.gallery)).catch(() => setItems([]))
  }, [])

  const update = (i: number, patch: Partial<GalleryItem>) =>
    setItems((cur) => (cur ? cur.map((it, idx) => (idx === i ? {...it, ...patch} : it)) : cur))
  const remove = (i: number) => setItems((cur) => (cur ? cur.filter((_, idx) => idx !== i) : cur))
  const move = (i: number, dir: -1 | 1) =>
    setItems((cur) => {
      if (!cur) return cur
      const j = i + dir
      if (j < 0 || j >= cur.length) return cur
      const copy = [...cur]
      ;[copy[i], copy[j]] = [copy[j], copy[i]]
      return copy
    })

  // Upload a file; with no index it appends a new tile, with an index it
  // replaces that tile's image (e.g. swap in the latest bathroom photo).
  const onPickImage = async (file?: File, index?: number) => {
    if (!file) return
    setUploading(true)
    try {
      const stored = await api.uploadFile(file)
      if (index === undefined) {
        setItems((cur) => [...(cur ?? []), {image: stored.key, label: 'New photo'}])
        toast.success('Photo added — set a label and save.')
      } else {
        update(index, {image: stored.key})
        toast.success('Photo replaced — remember to save.')
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const save = async () => {
    if (!items) return
    setSaving(true)
    try {
      const r = await api.updateSiteGallery(items.map((it) => ({image: it.image, label: it.label.trim() || 'Photo'})))
      setItems(r.gallery)
      toast.success('Gallery saved.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not save gallery')
    } finally {
      setSaving(false)
    }
  }

  if (!items) return <div><PageHead icon='🖼️' eyebrow='Content' title='Site Gallery' /><Spinner size={20} /></div>

  return (
    <div>
      <PageHead icon='🖼️' eyebrow='Content' title='Site Gallery' sub='The "Inside the crashpad" photos shown on the Home and Accommodations pages.' />

      <div className='ccp-stagger' style={{maxWidth: 760}}>
        {items.length === 0 && (
          <div style={{color: MUTED, fontSize: 14, padding: '18px 20px', background: '#fff', border: '1px solid #eef0f4', borderRadius: 14, marginBottom: 14}}>
            No photos yet — add one below.
          </div>
        )}

        {items.map((it, i) => (
          <div key={i} className='ccp-lift' style={{display: 'flex', alignItems: 'center', gap: 14, background: '#fff', border: '1px solid #eef0f4', borderRadius: 14, padding: '14px 16px', marginBottom: 12, boxShadow: '0 4px 12px rgba(27,27,41,0.04)'}}>
            <label title='Click to replace this photo' style={{position: 'relative', width: 96, height: 72, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb', flexShrink: 0, background: '#f4f6fa', cursor: uploading ? 'not-allowed' : 'pointer'}}>
              <img src={listingImageSrc(it.image)} alt={it.label} style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}} />
              <span style={{position: 'absolute', left: 0, right: 0, bottom: 0, padding: '3px 0', textAlign: 'center', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em'}}>REPLACE</span>
              <input type='file' accept='image/*' hidden disabled={uploading} onChange={(e) => onPickImage(e.target.files?.[0], i)} />
            </label>
            <div style={{flex: 1}}>
              <label style={{display: 'block', fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5}}>Caption</label>
              <input style={inputStyle} value={it.label} onChange={(e) => update(i, {label: e.target.value})} placeholder='e.g. Shared bathroom' />
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
              <button style={iconBtn} title='Move up' disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
              <button style={iconBtn} title='Move down' disabled={i === items.length - 1} onClick={() => move(i, 1)}>↓</button>
            </div>
            <button style={{...iconBtn, color: '#d9214e', borderColor: '#f3c6cf'}} title='Remove' onClick={() => remove(i)}>✕</button>
          </div>
        ))}

        <div style={{display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginTop: 6}}>
          <label style={{display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, color: INK, background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '11px 18px', cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1}}>
            {uploading ? <Spinner size={13} /> : '＋'} Add photo
            <input type='file' accept='image/*' hidden disabled={uploading} onChange={(e) => onPickImage(e.target.files?.[0])} />
          </label>
          <button onClick={save} disabled={saving} style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 26px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1}}>
            {saving ? <Spinner size={14} /> : 'Save gallery'}
          </button>
        </div>
        <p style={{fontSize: 12.5, color: MUTED, marginTop: 14}}>Drag order with ↑ / ↓. Changes go live on the public site as soon as you save.</p>
      </div>
    </div>
  )
}

export {SiteGalleryPage}
